import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Match, TimbaPrediction } from '../api/client';
import { TeamBadge } from './TeamBadge';
import { formatTime, countdown, weekdayShort, dayNum, monthShort } from '../lib/format';
import { venue, COUNTRY_ISO } from '../lib/venues';
import { formatPick } from '../lib/timba';
import { PointsBreakdown } from './PointsBreakdown';
import { useStore } from '../stores/store';

const Card = styled.button<{ $fav: boolean }>`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  text-align: left;
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme, $fav }) => ($fav ? theme.colors.gold : theme.colors.borderStrong)};
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px 14px;
  margin-bottom: 10px;
  ${({ $fav, theme }) => $fav && `box-shadow: 0 0 0 1px ${theme.colors.gold} inset;`}
`;

// Círculo con los puntos ganados, arriba a la derecha. Tocarlo abre el desglose.
const PointsCircle = styled.span<{ $hit: boolean }>`
  position: absolute;
  top: -9px;
  right: -7px;
  z-index: 2;
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: ${({ theme, $hit }) => ($hit ? theme.colors.hit : theme.colors.accent)};
  color: ${({ theme, $hit }) => ($hit ? '#000' : theme.colors.accentText)};
  font-weight: 900;
  font-size: 0.9rem;
  border: 2px solid ${({ theme }) => theme.colors.bg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const Mid = styled.div`
  text-align: center;
  min-width: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const ScoreBox = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: 1px;
`;

// Chip con estética calendario: día de semana arriba, número grande, mes.
const DateChip = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.05;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 3px 9px;
  .wd { font-size: 0.61rem; text-transform: uppercase; letter-spacing: 0.5px; color: ${({ theme }) => theme.colors.textMuted}; }
  .dn { font-size: 1.08rem; font-weight: 800; }
  .mo { font-size: 0.61rem; text-transform: uppercase; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Kick = styled.div`
  font-size: 0.86rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Live = styled.div`
  font-size: 0.66rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.live};
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.live};
    animation: pulse 1.2s infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const Foot = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 0.86rem;
  color: ${({ theme }) => theme.colors.text};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  padding-top: 8px;
  margin-top: 4px;

  img {
    width: 24px;
    height: 17px;
    border-radius: 3px;
    object-fit: cover;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.border};
  }
`;

// Barra inferior verde manzana cuando el partido tiene un pronóstico cargado. Sangra a los
// bordes de la card (anula el padding 12px 14px) y redondea las esquinas de abajo.
const TimbaBar = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 8px -14px -12px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.colors.pick};
  color: #06283a;
  border-radius: 0 0 11px 11px;

  .lbl {
    flex: 1;
    font-weight: 800;
    font-size: 0.97rem; /* ~2pt más grande que el texto normal de la card */
  }
  .boost, .llave {
    flex: 0 0 auto;
    font-size: 0.75rem;
    font-weight: 900;
    padding: 2px 7px;
    border-radius: 7px;
    background: rgba(0, 0, 0, 0.18);
    color: #000;
  }
  .pts {
    flex: 0 0 auto;
    min-width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text};
    border-radius: 8px;
    font-weight: 900;
    font-size: 0.99rem;
  }
`;

// ¿El favorito juega este partido? Considera tanto el equipo real como la proyección.
function involvesFavorite(m: Match, favorite: string | null): boolean {
  if (!favorite) return false;
  const a = m.team1.resolved ?? m.team1.name;
  const b = m.team2.resolved ?? m.team2.name;
  return a === favorite || b === favorite;
}

export function MatchCard({ m, showVenue = true, pred }: { m: Match; showVenue?: boolean; pred?: TimbaPrediction }) {
  const navigate = useNavigate();
  const favorite = useStore((s) => s.favorite);
  const v = showVenue ? venue(m.ground) : null;
  const [showBreak, setShowBreak] = useState(false);

  return (
    <Card $fav={involvesFavorite(m, favorite)} onClick={() => navigate(`/match/${m.id}`)}>
      {pred?.points != null && (
        <PointsCircle
          $hit={(pred?.points ?? 0) > 0}
          role="button"
          title="Ver cómo se sumaron estos puntos"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowBreak(true);
          }}
        >
          {pred.points}
        </PointsCircle>
      )}
      {showBreak && pred && (
        <PointsBreakdown pred={pred} t1={m.team1.resolved} t2={m.team2.resolved} onClose={() => setShowBreak(false)} />
      )}

      <TeamBadge team={m.team1} align="left" />

      <Mid>
        {m.score ? (
          <ScoreBox>
            {m.score.ft[0]}{m.score.pen ? ` (${m.score.pen[0]})` : ''}–{m.score.ft[1]}{m.score.pen ? ` (${m.score.pen[1]})` : ''}
          </ScoreBox>
        ) : (
          <DateChip>
            <span className="wd">{weekdayShort(m.kickoffUtc)}</span>
            <span className="dn">{dayNum(m.kickoffUtc)}</span>
            <span className="mo">{monthShort(m.kickoffUtc)}</span>
          </DateChip>
        )}
        <Kick>{formatTime(m.kickoffUtc)}</Kick>
        {m.status === 'live' && <Live>EN JUEGO{m.live?.clock ? ` · ${m.live.clock}` : ''}</Live>}
        {m.status === 'upcoming' && <Sub>{countdown(m.kickoffUtc)}</Sub>}
      </Mid>

      <TeamBadge team={m.team2} align="right" />

      {v && (
        <Foot>
          <img src={`https://flagcdn.com/w160/${COUNTRY_ISO[v.country]}.png`} alt={v.country} loading="lazy" />
          {v.stadium} · {v.city}
        </Foot>
      )}

      {pred && (
        <TimbaBar>
          <span className="lbl">Tu pronóstico {formatPick(pred, m.team1.resolved, m.team2.resolved)}</span>
          {pred.booster != null && pred.booster > 1 && <span className="boost">×{pred.booster}</span>}
          {pred.bonus != null && pred.bonus > 0 && <span className="llave">🔑 +{pred.bonus}</span>}
        </TimbaBar>
      )}
    </Card>
  );
}
